<template>
  <label
    :class="componentClassName"
    @drop="handleDrop"
    @dragenter="handleDragging"
    @dragover="handleDragging"
    @dragleave="handleDragLeave"
  >
    <div class="a-upload-field__dropzone"></div>
    <input ref="inputRef" v-bind="inputAttrs" type="file" class="a-upload-field__input" @change="handleInputChange" />
    <figure class="a-upload-field__body">
      <div class="a-upload-field__preview">
        <img v-if="imageUrl" :src="imageUrl" class="img_upload" />
        <div v-else class="a-upload-field__preview-text">
          <a-text v-if="fileInfo" color="orange">
            {{ fileInfo.name }}<br />
            {{ fileSizeText }}
          </a-text>
        </div>
        <button class="a-upload-field__clear-button" @click="handleClearButtonClick">
          <a-icon icon="close" />
        </button>
      </div>
      <div class="a-upload-field__placeholder">
        <div class="a-upload-field__label">
          <a-text color="orange">{{ label }}</a-text>
        </div>
        <div class="a-upload-field__button">
          <a-button
            id="uploadFieldBtn"
            variant="fill"
            color="primary"
            size="small"
            icon="upload"
            icon-color="white"
            label="アップロード"
          />
        </div>
      </div>
    </figure>
  </label>
</template>

<script>
import numeral from 'numeral';
import { mapModifiers } from '@/libs/component';
import AButton from '@/components/atoms/button';
import AIcon from '@/components/atoms/icon';
import AText from '@/components/atoms/text';

export default {
  name: 'a-upload-field',
  components: {
    AButton,
    AIcon,
    AText,
  },
  props: {
    class: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [Array, String], // String -> existing URL file for display
      default: null,
    },
  },
  emits: ['change', 'update:modelValue'],
  data() {
    return {
      imageUrl: '',
      fileInfo: null,
      isDragging: false,
      currentValue: [],
      closeButton: false,
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers(
        'a-upload-field',
        this.hasImage ? 'has-image' : '',
        this.isDragging ? 'dragging' : ''
      );
      return `${baseClassName} ${this.class}`.trim();
    },
    disabled() {
      return this.$attrs.disabled;
    },
    inputAttrs() {
      const { ...inputAttrs } = this.$attrs;
      return inputAttrs;
    },
    hasImage() {
      return this.imageUrl || this.fileInfo;
    },
    fileSizeText() {
      return `(${numeral(this.fileInfo.size).format('0,0')} bytes)`;
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.setCurrentValue(val);
      } else {
        this.handleClearButtonClick();
      }
    },
  },
  mounted() {
    if (this.modelValue) {
      this.setCurrentValue(this.modelValue);
    }
  },
  methods: {
    handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = false;
      const inputRef = this.$refs.inputRef;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && inputRef) {
        // Validate if the dropped file type is accepted

        // Only take first file in the case of dropping multiple files
        const dt = new DataTransfer();
        dt.items.add(e.dataTransfer.files[0]);
        const inputType = dt.files[0].type.toLowerCase();
        const fileName = dt.files[0].name;
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'), fileName.length);
        const inputTypeParts = inputType.split('/');
        const acceptedTypes = this.inputAttrs.accept.split(',').map(type => {
          return type.trim();
          //          return type.trim().toLowerCase();
        });
        if (acceptedTypes && acceptedTypes.length > 0) {
          // Check each accept type
          for (let i = 0; i < acceptedTypes.length; i++) {
            const acceptedTypeParts = acceptedTypes[i].split('/');
            if (
              acceptedTypes[i] === inputType ||
              acceptedTypes[i] === fileExtension ||
              (acceptedTypeParts.length > 0 &&
                acceptedTypeParts[1] === '*' &&
                acceptedTypeParts[0] === inputTypeParts[0])
            ) {
              // Dropped file type accepted
              inputRef.files = dt.files;
              inputRef.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
            // Dropped file type does not match current accept type
          }
        }
      }
      // Dropped input type rejected
    },
    handleDragging(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = true;
    },
    handleDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = false;
    },
    handleClearButtonClick(e) {
      this.closeButton = true;
      if (e) e.preventDefault();
      const inputRef = this.$refs.inputRef;
      if (inputRef) {
        inputRef.value = '';
        inputRef.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    handleInputChange(e) {
      // ファイル選択ダイアログでキャンセルが押されたとき、何もしないで処理を抜ける
      if (!this.closeButton && e.target.files.length === 0) {
        return;
      }
      this.closeButton = false;

      this.setCurrentValue(e.target.files || []);
      if (e.target.files && e.target.files.length > 0) {
        let set_url;
        const file = e.target.files[0];
        this.fileInfo = file;
        // check image file
        if (file.type.indexOf('image/') !== -1) {
          const fileReader = new FileReader();
          fileReader.onload = function () {
            const imageUrl = 'data:image/png;base64,' + btoa(this.result);
            const img_elements = document.getElementsByClassName('img_upload');

            for (let i = 0; i < img_elements.length; i++) {
              if (img_elements[i].src === set_url) {
                img_elements[i].src = imageUrl;
              }
            }
          };
          fileReader.readAsBinaryString(file);
          this.imageUrl = URL.createObjectURL(file);
          set_url = this.imageUrl;
        } else {
          this.imageUrl = '';
        }
      } else {
        this.imageUrl = '';
        this.fileInfo = null;
      }
      this.$emit('update:modelValue', this.currentValue);
      this.$emit('change', this.currentValue);
    },
    setCurrentValue(val) {
      if (typeof val !== 'string') {
        this.currentValue.splice(0);
        if (val) {
          val.forEach(item => {
            this.currentValue.push(item);
          });
        }
      } else {
        // use for display
        this.imageUrl = val;
      }
    },
  },
};
</script>
